import React, { useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tab,
  Tabs,
  Avatar,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Add,
  Close,
  CheckCircle,
  Cancel,
  Pending,
  CalendarMonth,
  AccessTime,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useMyLeaves, useApplyLeave, useLeaveBalance, useLeaveManageTypes } from "../../hooks/queries/useLeaves";
import PrimaryButton from "../../components/ui/PrimaryButton";
import dayjs from "dayjs";

const Leaves = () => {
  const { user } = useSelector((state) => state.auth);
  const { data: leavesData, isLoading: leavesLoading } = useMyLeaves({});
  const { data: leaveBalance, isLoading: balanceLoading } = useLeaveBalance(user?.id);
  const applyMutation = useApplyLeave();
  const leaves = leavesData?.leaves || leavesData || [];
  const loading = leavesLoading || balanceLoading;
  const [activeTab, setActiveTab] = useState(0);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [formData, setFormData] = useState({
    leaveManageId: "",
    startDate: null,
    endDate: null,
    leaveDurationType: "FULL_DAY",
    halfDayPeriod: "FIRST_HALF",
    reason: "",
  });
  const [submitError, setSubmitError] = useState("");

  // Sample data for demo
  const sampleLeaves = [
    {
      id: 1,
      type: "Sick Leave",
      startDate: "2025-12-19",
      endDate: "2025-12-20",
      days: 2,
      reason: "Fever and cold",
      status: "approved",
      appliedOn: "2025-12-17",
      approvedBy: "Ms. Davis",
    },
    {
      id: 2,
      type: "Personal Leave",
      startDate: "2025-12-25",
      endDate: "2025-12-26",
      days: 2,
      reason: "Family function",
      status: "pending",
      appliedOn: "2025-12-15",
      approvedBy: null,
    },
    {
      id: 3,
      type: "Medical Leave",
      startDate: "2025-11-10",
      endDate: "2025-11-12",
      days: 3,
      reason: "Doctor appointment",
      status: "approved",
      appliedOn: "2025-11-08",
      approvedBy: "Dr. Smith",
    },
    {
      id: 4,
      type: "Casual Leave",
      startDate: "2025-10-15",
      endDate: "2025-10-15",
      days: 1,
      reason: "Personal work",
      status: "rejected",
      appliedOn: "2025-10-12",
      approvedBy: null,
      remarks: "Exam period - leave not allowed",
    },
  ];

  const sampleBalance = {
    casual: { total: 10, used: 3, remaining: 7 },
    sick: { total: 15, used: 5, remaining: 10 },
    medical: { total: 20, used: 3, remaining: 17 },
    personal: { total: 5, used: 2, remaining: 3 },
  };

  const displayLeaves = leaves.length > 0 ? leaves : sampleLeaves;
  const displayBalance = leaveBalance || sampleBalance;

  const { data: manageTypesData } = useLeaveManageTypes();
  const fetchedManageTypes = manageTypesData?.data ?? manageTypesData ?? [];
  const activeManageTypes = Array.isArray(fetchedManageTypes)
    ? fetchedManageTypes.filter((t) => t?.status !== false && !t?.isDeleted)
    : [];

  const resolveLeaveManageId = (type) =>
    type?.manageId ??
    type?.leaveManageId ??
    type?.leaveTypeManageId ??
    type?.id ??
    type?._id ??
    type?.uuid ??
    "";

  const leaveManageTypeOptions = activeManageTypes
    .map((t) => {
      const leaveManageId = resolveLeaveManageId(t);
      const label = t?.leaveTypeName || t?.name || t?.label || t?.type || "";
      return leaveManageId ? { ...t, leaveManageId, label } : null;
    })
    .filter(Boolean);

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle />;
      case "pending":
        return <Pending />;
      case "rejected":
        return <Cancel />;
      default:
        return <AccessTime />;
    }
  };

  const normalizedLeaves = React.useMemo(() => {
    const resolveTypeLabel = (raw) => {
      const typeRaw =
        raw?.leaveType ||
        raw?.type ||
        raw?.LeaveManage?.leaveTypeName ||
        raw?.leaveManage?.leaveTypeName ||
        "";
      const normalized = String(typeRaw || "").trim();
      if (!normalized) return "—";
      const match = leaveManageTypeOptions.find(
        (t) => String(t.label || "").trim().toLowerCase() === normalized.toLowerCase()
      );
      return match?.label || normalized;
    };

    return (Array.isArray(displayLeaves) ? displayLeaves : []).map((leave) => ({
      id: leave?.id ?? leave?._id,
      type: resolveTypeLabel(leave),
      startDate: leave?.leaveFrom || leave?.startDate || leave?.fromDate || "-",
      endDate: leave?.leaveTo || leave?.endDate || leave?.toDate || "-",
      days: leave?.duration ?? leave?.days ?? 0,
      reason: leave?.notes || leave?.reason || leave?.description || "-",
      status: leave?.status || "pending",
      appliedOn: leave?.createdAt || leave?.appliedOn || null,
      approvedBy: leave?.approvedBy || null,
      approvedOn: leave?.approvedOn || null,
      rejectedOn: leave?.rejectedOn || null,
      remarks: leave?.rejectionReason || leave?.remarks || null,
      document: leave?.document || leave?.attachment || null,
      raw: leave,
    }));
  }, [displayLeaves, leaveManageTypeOptions]);

  const filteredLeaves = normalizedLeaves.filter((leave) => {
    if (activeTab === 0) return true;
    if (activeTab === 1) return leave.status === "pending";
    if (activeTab === 2) return leave.status === "approved";
    if (activeTab === 3) return leave.status === "rejected";
    return true;
  });

  const formatDateValue = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value?.format === "function") return value.format("YYYY-MM-DD");
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().split("T")[0];
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD MMM YYYY, hh:mm A") : String(value);
  };

  const handleSubmit = async () => {
    setSubmitError("");
    const isHalfDay = formData.leaveDurationType === "HALF_DAY";
    const leaveFrom = formatDateValue(formData.startDate);
    const payload = {
      leaveFrom,
      leaveTo: isHalfDay ? leaveFrom : formatDateValue(formData.endDate),
      leaveManageId: formData.leaveManageId,
      notes: formData.reason,
      leaveDurationType: formData.leaveDurationType || "FULL_DAY",
      ...(isHalfDay ? { halfDayPeriod: formData.halfDayPeriod || "FIRST_HALF" } : {}),
    };
    try {
      // Enforce advance notice rule (fast feedback; backend also validates)
      const selectedType = leaveManageTypeOptions.find(
        (t) => String(t.leaveManageId) === String(formData.leaveManageId)
      );
      const noticeDays = Math.max(
        0,
        parseInt(
          selectedType?.advanceNoticeDays ?? selectedType?.advanceNoticeDay ?? 0,
          10
        ) || 0
      );
      if (noticeDays > 0 && formData.startDate) {
        const diffDays = dayjs(formData.startDate)
          .startOf("day")
          .diff(dayjs().startOf("day"), "day");
        if (diffDays < noticeDays) {
          setSubmitError(
            `You must apply at least ${noticeDays} day(s) in advance for this leave type.`
          );
          return;
        }
      }

      await applyMutation.mutateAsync(payload);
      setApplyDialogOpen(false);
      setFormData({ leaveManageId: "", startDate: null, endDate: null, reason: "" });
      setSubmitError("");
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to apply leave. Please check your selection and try again.";
      setSubmitError(apiMessage);
    }
  };

  const columns = [
    {
      field: "type",
      headerName: "Leave Type",
      width: 150,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: "startDate",
      headerName: "From",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarMonth sx={{ fontSize: 16, color: "text.secondary" }} />
          {params.value}
        </Box>
      ),
    },
    { field: "endDate", headerName: "To", width: 130 },
    { field: "days", headerName: "Days", width: 80 },
    { field: "reason", headerName: "Reason", flex: 1, minWidth: 200 },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.value)}
          icon={getStatusIcon(params.value)}
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
    { field: "appliedOn", headerName: "Applied On", width: 130 },
    {
      field: "actions",
      headerName: "View",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() => {
            setSelectedLeave(params.row);
            setViewDialogOpen(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Leave Applications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Apply for leaves and track your applications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setApplyDialogOpen(true)}
        >
          Apply Leave
        </Button>
      </Box>

      {/* Leave Balance Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Object.entries(displayBalance).map(([type, balance]) => (
          <Grid item xs={6} sm={3} key={type}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "capitalize" }}
                >
                  {type} Leave
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {balance.remaining}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of {balance.total} remaining
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: "grey.200",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${(balance.remaining / balance.total) * 100}%`,
                        bgcolor: "primary.main",
                        borderRadius: 2,
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Card */}
      <Card>
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 3 }}
          >
            <Tab label="All" />
            <Tab label="Pending" />
            <Tab label="Approved" />
            <Tab label="Rejected" />
          </Tabs>

          <DataGrid
            rows={filteredLeaves}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            loading={loading}
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0" },
            }}
          />
        </CardContent>
      </Card>

      {/* Apply Leave Dialog */}
      <Dialog
        open={applyDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setApplyDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Apply for Leave
            <IconButton onClick={() => setApplyDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {submitError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          ) : null}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Leave Type</InputLabel>
                <Select
                  value={formData.leaveManageId}
                  label="Leave Type"
                  onChange={(e) =>
                    setFormData({ ...formData, leaveManageId: e.target.value })
                  }
                >
                  {leaveManageTypeOptions.map((type) => (
                    <MenuItem
                      key={String(type.leaveManageId)}
                      value={String(type.leaveManageId)}
                    >
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={formData.leaveDurationType}
                  label="Duration"
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      leaveDurationType: v,
                      endDate: v === "HALF_DAY" ? prev.startDate : prev.endDate,
                    }));
                  }}
                >
                  <MenuItem value="FULL_DAY">Full day</MenuItem>
                  <MenuItem value="HALF_DAY">Half day</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.leaveDurationType === "HALF_DAY" && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Half</InputLabel>
                  <Select
                    value={formData.halfDayPeriod}
                    label="Half"
                    onChange={(e) => setFormData({ ...formData, halfDayPeriod: e.target.value })}
                  >
                    <MenuItem value="FIRST_HALF">First half</MenuItem>
                    <MenuItem value="SECOND_HALF">Second half</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={formData.leaveDurationType === "HALF_DAY" ? 12 : 6}>
              <DatePicker
                label={formData.leaveDurationType === "HALF_DAY" ? "Date" : "Start Date"}
                value={formData.startDate}
                onChange={(date) =>
                  setFormData((prev) => ({
                    ...prev,
                    startDate: date,
                    endDate: prev.leaveDurationType === "HALF_DAY" ? date : prev.endDate,
                  }))
                }
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            {formData.leaveDurationType !== "HALF_DAY" && (
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(date) => setFormData({ ...formData, endDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={4}
                value={formData.reason}
                onChange={(e) =>
                  setFormData({ ...formData, reason: e.target.value })
                }
                placeholder="Please provide a detailed reason for your leave request..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={
              !formData.leaveManageId ||
              !formData.startDate ||
              (formData.leaveDurationType !== "HALF_DAY" && !formData.endDate) ||
              !formData.reason
            }
          >
            Submit Application
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* View Leave Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Leave Details</Typography>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLeave && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                  <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.main" }}>
                    {(user?.fullName || user?.name || "U")[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {user?.fullName || user?.name || "—"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLeave.type}
                    </Typography>
                  </Box>
                  <Chip
                    label={selectedLeave.status}
                    size="small"
                    color={getStatusColor(selectedLeave.status)}
                    icon={getStatusIcon(selectedLeave.status)}
                    sx={{ ml: "auto", textTransform: "capitalize" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">From</Typography>
                <Typography variant="body2" fontWeight={500}>{selectedLeave.startDate}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">To</Typography>
                <Typography variant="body2" fontWeight={500}>{selectedLeave.endDate}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2" fontWeight={500}>{selectedLeave.days} day(s)</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Applied On</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDateTime(selectedLeave.raw?.createdAt || selectedLeave.appliedOn)}
                </Typography>
              </Grid>

              {(selectedLeave.status === "approved" || selectedLeave.status === "rejected") && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedLeave.status === "approved" ? "Approved By" : "Rejected By"}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedLeave.approvedBy?.fullName ||
                        selectedLeave.approvedBy?.name ||
                        selectedLeave.approvedBy ||
                        "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedLeave.status === "approved" ? "Approved On" : "Rejected On"}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDateTime(
                        selectedLeave.status === "approved"
                          ? selectedLeave.approvedOn
                          : selectedLeave.raw?.rejectedOn || selectedLeave.rejectedOn
                      )}
                    </Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography variant="body2">{selectedLeave.reason}</Typography>
              </Grid>

              {selectedLeave.document && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Document</Typography>
                  <Typography
                    component="a"
                    href={selectedLeave.document}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" }, display: "block", mt: 0.5 }}
                  >
                    View Document
                  </Typography>
                </Grid>
              )}

              {selectedLeave.remarks && selectedLeave.status === "rejected" && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      <strong>Rejection Reason:</strong> {selectedLeave.remarks}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leaves;
